import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Download } from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { useCurrency } from '../hooks/useCurrency';
import { downloadCSV } from '../lib/csv';
import { ReportFilters } from '../components/reports/ReportFilters';
import { ReportSummaryCards } from '../components/reports/ReportSummaryCards';
import { LifetimeBalances } from '../components/reports/LifetimeBalances';
import { WageReportTable } from '../components/reports/WageReportTable';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyTotals = {
  total_days: 0,
  full_days: 0,
  half_days: 0,
  absent_days: 0,
  overtime_hours: 0,
  gross_wage: 0,
  advance_deduction: 0,
  payout_total: 0,
  net_wage: 0
};

const resolveQuickRange = (range) => {
  const today = new Date();
  switch (range) {
    case 'today':
      return { start: today, end: today };
    case 'week':
      return { start: subDays(today, 7), end: today };
    case 'month':
      return { start: startOfMonth(today), end: today };
    case 'last_month': {
      const lastMonth = subDays(startOfMonth(today), 1);
      return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
    }
    default:
      return null;
  }
};

const Reports = () => {
  const [workers, setWorkers] = useState([]);
  const [wageReport, setWageReport] = useState([]);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState('all');
  const [dateRange, setDateRange] = useState({
    start_date: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd')
  });
  const { format: fmt, currency } = useCurrency();

  const fetchWorkers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/workers`);
      setWorkers(response.data);
    } catch (error) {
      toast.error('Failed to load workers');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBalances = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/reports/balances`);
      setBalances(response.data);
    } catch (error) {
      toast.error('Failed to load lifetime balances');
    }
  }, []);

  const fetchWageReport = useCallback(async () => {
    setReportLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: dateRange.start_date,
        end_date: dateRange.end_date
      });
      if (selectedWorker !== 'all') {
        params.append('worker_id', selectedWorker);
      }
      const response = await axios.get(`${API_URL}/api/reports/wages?${params}`);
      setWageReport(response.data);
    } catch (error) {
      toast.error('Failed to load wage report');
    } finally {
      setReportLoading(false);
    }
  }, [dateRange, selectedWorker]);

  useEffect(() => {
    fetchWorkers();
    fetchBalances();
  }, [fetchWorkers, fetchBalances]);

  useEffect(() => {
    fetchWageReport();
  }, [fetchWageReport]);

  const setQuickDateRange = (range) => {
    const resolved = resolveQuickRange(range);
    if (!resolved) return;
    setDateRange({
      start_date: format(resolved.start, 'yyyy-MM-dd'),
      end_date: format(resolved.end, 'yyyy-MM-dd')
    });
  };

  const totals = wageReport.reduce((acc, r) => ({
    total_days: acc.total_days + r.total_days,
    full_days: acc.full_days + r.full_days,
    half_days: acc.half_days + r.half_days,
    absent_days: acc.absent_days + r.absent_days,
    overtime_hours: acc.overtime_hours + r.overtime_hours,
    gross_wage: acc.gross_wage + r.gross_wage,
    advance_deduction: acc.advance_deduction + r.advance_deduction,
    payout_total: acc.payout_total + r.payout_total,
    net_wage: acc.net_wage + r.net_wage
  }), emptyTotals);

  const exportToCSV = () => {
    if (wageReport.length === 0) {
      toast.error('No data to export');
      return;
    }

    downloadCSV({
      headers: ['Worker', 'Total Days', 'Full Days', 'Half Days', 'Absent', 'Overtime (hrs)', `Gross Wage (${currency.code})`, `Advance Deduction (${currency.code})`, `Payout (${currency.code})`, 'Payment Dates', `Net Wage (${currency.code})`],
      rows: wageReport.map(r => [
        r.worker_name,
        r.total_days,
        r.full_days,
        r.half_days,
        r.absent_days,
        r.overtime_hours,
        r.gross_wage,
        r.advance_deduction,
        r.payout_total,
        (r.payout_dates || []).join('; '),
        r.net_wage
      ]),
      filename: `wage_report_${dateRange.start_date}_to_${dateRange.end_date}.csv`
    });
    toast.success('Report exported successfully');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="reports-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2E4632]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1
            className="text-3xl md:text-4xl font-bold text-[#1C1C1E] tracking-tight"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Reports
          </h1>
          <p className="text-[#5C5C5C] mt-1">
            View wage and attendance reports
          </p>
        </div>

        <Button
          onClick={exportToCSV}
          data-testid="export-report-btn"
          className="h-12 px-6 bg-[#2E4632] hover:bg-[#1F3122] text-white font-semibold"
        >
          <Download className="w-5 h-5 mr-2" />
          Export CSV
        </Button>
      </div>

      <ReportFilters
        workers={workers}
        selectedWorker={selectedWorker}
        setSelectedWorker={setSelectedWorker}
        dateRange={dateRange}
        setDateRange={setDateRange}
        onQuickRange={setQuickDateRange}
      />

      <ReportSummaryCards workerCount={wageReport.length} totals={totals} fmt={fmt} />

      <LifetimeBalances balances={balances} fmt={fmt} />

      <WageReportTable
        wageReport={wageReport}
        totals={totals}
        fmt={fmt}
        reportLoading={reportLoading}
        dateRange={dateRange}
      />
    </div>
  );
};

export default Reports;
